<template lang="html">
  <header
    v-tracking-context="{ content_element: 'ad_meta', id: job.id }"
    class="fsd-job-meta"
  >
    <span
      v-if="!isPreview"
      v-click-track="{
        action_type: `bookmark_toggle_${bookmarked ? 'off' : 'on'}`
      }"
      class="fsd-job-teaser__bookmark"
      :class="{ 'fsd-job-teaser__bookmark--on': bookmarked }"
      @click.stop.prevent="onBookmarkToggleClick"
    >
      <svg-icon
        icon-name="bookmark"
        class-name="fsd-job-teaser__bookmark-flag"
      />
    </span>
    <mark v-if="job.data.topJob" class="fsd-job-meta__top-job">Top Job</mark>

    <component :is="headingLevel" class="fsd-job-meta__title">
      {{ job.data.title }}
    </component>

    <div v-if="showEmployerSubtitle" class="fsd-job-meta__subtitle">
      {{ job.data.employer }}
    </div>

    <div
      v-if="showJobReference && job.data.referenceId"
      class="fsd-job-meta__job-reference"
    >
      {{ job.data.referenceId }}
    </div>

    <div class="fsd-job-meta__wrapper">
      <dl
        class="fsd-job-meta__list"
        :class="{
          'fsd-job-meta__list--narrow':
            (employerLogoUrlVisible || job.data.matchingProfile) &&
            !isMapVisible
        }"
      >
        <div
          v-for="item in presentItems"
          :key="item.key"
          class="fsd-job-meta__list-item"
        >
          <dt class="fsd-job-meta__list-term">
            <img
              class="fsd-job-meta__list-icon"
              :src="require('images/jobs/' + item.icon + '.svg')"
              alt=""
            />
            <template v-if="item.label">
              <span class="fsd-job-meta__list-text">{{ item.label | t }}</span>
            </template>
            <template v-else>
              <span class="fsd-job-meta__list-text">{{
                job.data[item.key]
              }}</span>
            </template>
          </dt>
          <template v-if="item.label">
            <dd class="fsd-job-meta__list-value">
              <span class="fsd-job-meta__list-text">{{
                job.data[item.key]
              }}</span>
            </dd>
          </template>
        </div>

        <div v-if="!showEmployerSubtitle" class="fsd-job-meta__list-item">
          <dt class="fsd-job-meta__list-term">
            <img
              class="fsd-job-meta__list-icon"
              :src="hiringCompanySvgPath"
              alt=""
            />
          </dt>
          <dd class="fsd-job-meta__list-value">
            {{ job.data.employer }}
          </dd>
        </div>

        <div
          v-if="employerLogoUrlVisible || job.data.matchingProfile"
          class="fsd-job-meta__list-item fsd-job-meta__list-item--collapsable"
        >
          <dt v-if="employerLogoUrlVisible" class="fsd-job-meta__list-term">
            <img
              class="fsd-job-meta__employer-logo"
              :src="employerLogoUrl"
              :alt="logoAltText"
            />
          </dt>

          <dt v-if="job.data.matchingProfile" class="fsd-job-meta__list-term">
            <a
              v-click-track="{
                action_type: 'employer_visit',
                target: job.data.matchingProfile
              }"
              class="fsd-job-meta__employer-button"
              :href="job.data.matchingProfile"
            >
              {{ 'client.fused.job.go_to_employer' | t }}
            </a>
          </dt>
        </div>

        <div class="fsd-job-meta__list-item fsd-job-meta__list-item">
          <dt class="fsd-job-meta__list-term">
            <img
              class="fsd-job-meta__list-icon"
              :src="locationSvgPath"
              alt=""
            />
          </dt>
          <dd class="fsd-job-meta__list-value fsd-job-meta__list-value">
            {{ job.data.employerLocation }}
            <google-maps
              v-if="job.data.hasGoogleMaps"
              :label="job.data.employer"
              :query="job.data.googleMapsQuery"
              @toggled="this.onMapToggle"
            />
          </dd>
        </div>
      </dl>
    </div>
  </header>
</template>

<script>
import _get from 'lodash-es/get'
import _debounce from 'lodash-es/debounce'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import GoogleMaps from '@/vue/components/fused/google_maps'
import hiringCompanySvgPath from 'images/jobs/hiring_company.svg'
import locationSvgPath from 'images/jobs/location.svg'

export default {
  components: { GoogleMaps },
  props: {
    job: { type: Object, required: true },
    items: { type: Array, required: true },
    forceHighestHeaderLevel: { type: Boolean, default: false },
    showEmployerLogo: { type: Boolean, default: false },
    showJobReference: { type: Boolean, default: false },
    showEmployerSubtitle: { type: Boolean, default: false },
    isPreview: { type: Boolean, default: false }
  },
  data: () => ({
    hiringCompanySvgPath,
    locationSvgPath,
    isMapVisible: null
  }),
  computed: {
    ...mapGetters(['bookmarksMap', 'isUserLoggedIn', 'isUserKnown']),
    headingLevel() {
      if (this.forceHighestHeaderLevel) {
        return 'h1'
      } else if (_get(this.job, 'data.hasOwnTitle')) {
        return 'h2'
      }
      return 'h1'
    },
    presentItems() {
      return this.items.filter(({ key }) => _get(this.job, `data.${key}`))
    },
    bookmarked() {
      return !!this.bookmarksMap[this.job.id]
    },
    employerLogoUrlVisible() {
      return !!this.showEmployerLogo && !!this.employerLogoUrl
    },
    employerLogoUrl() {
      return _get(this.job, 'data.logo')
    },
    logoAltText() {
      return `${_get(this.job, 'data.title')} - ${_get(
        this.job,
        'data.employer'
      )} - Logo`
    }
  },
  methods: {
    ...mapActions(['bookmarksToggle']),
    ...mapMutations(['modalOpen']),
    onMapToggle(isMapVisible) {
      this.isMapVisible = isMapVisible
    },
    onBookmarkToggleClick: _debounce(
      function () {
        if (!this.job) {
          return
        }
        if (this.isUserLoggedIn) {
          this.bookmarksToggle(this.job.id)
        } else {
          EventBus.trigger('sessionModal:open', {
            intent: 'toggleBookmark',
            intentData: this.job.id
          })
        }
      },
      300,
      {
        leading: true,
        trailing: false
      }
    )
  }
}
</script>
